import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import getRelativeTime from '@/constants/utils/timestamp';

// Opaque "[Plus-only result]" placeholder row shown in sidebar search
// results for churned-Plus users when the match hits a conversation now
// locked behind the paywall. See PLUS_WINBACK_PRESSURE.md §1:
//   "Searches return `[Plus-only result]` placeholders for hidden convos
//    so they *know* the data is still there."
//
// IMPORTANT: this row never receives the locked conversation's title or
// content from the backend — the placeholder payload is just `id`,
// `updatedAt`, `createdAt`. Treat it as a pure pressure surface, not a
// navigable row: tapping it opens the upgrade modal, never the chat.
const createStyles = (colors, isHovered) => StyleSheet.create({
  container: {
    width: '100%',
    height: 32,
    minHeight: 32,
  },
  inner: {
    flex: 1,
    paddingLeft: 10,
    paddingRight: 10,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    backgroundColor: isHovered ? colors.solid_color_tertiary : 'transparent',
  },
  lockIcon: {
    marginRight: 8,
    marginLeft: -2,
  },
  title: {
    flex: 1,
    minWidth: 0,
    color: colors.text_color_tertiary,
    fontStyle: 'italic',
  },
  timestamp: {
    marginLeft: 4,
    color: colors.text_color_tertiary,
    flexShrink: 0,
  },
});

function SidebarPlusLockedResultRow({ id, updatedAt }) {
  const { colors } = useTheme();
  const [isHovered, setIsHovered] = React.useState(false);
  const styles = useMemo(() => createStyles(colors, isHovered), [colors, isHovered]);
  const { openModal } = useModalStore(state => state.actions);

  const handlePress = useCallback(() => {
    openModal('CreditsPurchaseModal', { initialTab: 'subscribe', source: 'plus-lock-search' });
  }, [openModal]);

  const relative = updatedAt ? getRelativeTime(updatedAt) : '';

  const hoverProps = Platform.OS === 'web' ? {
    onHoverIn: () => setIsHovered(true),
    onHoverOut: () => setIsHovered(false),
  } : {};

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel="Restore Plus to view this conversation"
      style={styles.container}
      {...hoverProps}
    >
      <View style={styles.inner}>
        <Icon
          name="lock"
          size={12}
          color={colors.text_color_tertiary}
          style={styles.lockIcon}
          solid
        />
        <Text size="n" numberOfLines={1} style={styles.title}>
          [Plus-only result]
        </Text>
        {relative ? (
          <Text size="xs" style={styles.timestamp}>
            {relative}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
}

SidebarPlusLockedResultRow.propTypes = {
  id: PropTypes.string.isRequired,
  updatedAt: PropTypes.string,
};

SidebarPlusLockedResultRow.defaultProps = {
  updatedAt: null,
};

export default React.memo(SidebarPlusLockedResultRow);
